/**
 * ChatRunSteps — the V2 live/frozen run-step timeline (bead
 * terra-design-v2-chat-yub.9, spec p1.s7, "Render the live run steps").
 * Supersedes the bead .1 scaffold placeholder (a bare "Reasoning · N steps"
 * count) with the real Glaze `ChainOfThought` rendering.
 *
 * A pure presentational component: given a `turnId` and the caller-resolved
 * `outcome`, it reads the turn record through `useTransparencyTurn` (bead
 * .8's shared side-channel — "the steps context reader", the ONE data hook
 * this file calls) and renders. It derives no steps of its own and reads no
 * stream directly — see coding-standards.md's "Derivation belongs in a hook
 * shared by V1 and V2" rule and this bead's own "Do NOT Create" list.
 *
 * `outcome` is caller-computed (mirroring `ChatMarkdown`'s own
 * caller-computes-streaming convention) because distinguishing "stopped"
 * from "running" needs the thread-level `isRunning` signal, which this
 * turn-scoped record does not carry — see `ChatThreadV2.tsx` for how it's
 * resolved. `'error'` comes from the turn record's own `errored` flag
 * (bead .9's additive field — see `deriveTransparencyTurns.ts`), since an
 * `error` frame never replaces the never-finalized assistant placeholder
 * the way a final `assistant` frame does.
 */
import { useEffect, useState } from 'react';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
  type ChainOfThoughtStepStatus,
} from '@clay-run/glaze/chain-of-thought';
import { Text } from '@clay-run/glaze/text';
import { TextLink } from '@clay-run/glaze/textlink';
import { FLOW_ICONS } from '../../icons';
import { useTransparencyTurn } from '../../../shared/TransparencyStepsContext';
import type { GenerationStep, TransparencyStep } from '../../../../types/transparency';
import { ChatMarkdown } from './ChatMarkdown';

// `transparency.ts` exports `GenerationStep` but not a dedicated
// `RetrievalStep` type (only its internal, un-exported schema) —
// `terra/no-parallel-schema-and-type` forbids hand-declaring a parallel
// shape, so narrow the canonical union instead of adding a new export.
type RetrievalStep = Extract<TransparencyStep, { type: 'retrieval' }>;

export type ChatRunStepsOutcome = 'running' | 'stopped' | 'error' | 'done';

export interface ChatRunStepsProps {
  turnId: string;
  outcome: ChatRunStepsOutcome;
}

// Real trace durations (not a compressed demo-playback scale) — the one
// place the original Keystone wall-clock numbers surface. 27864 -> "27.9s".
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// `TransparencyStep` has no dedicated error flag — the real trace's one
// failed step (an ENOENT read) is identifiable only by its `resultPreview`
// text starting with "Error". This heuristic is deliberately narrow (a real
// error field would replace it, not stack on top of it).
function isErroredStep(step: TransparencyStep): boolean {
  return step.type === 'retrieval' && /^error/i.test(step.resultPreview ?? '');
}

/**
 * Per-step `ChainOfThoughtStepStatus`. The ENOENT-style per-step error
 * always wins. Otherwise: while `outcome === 'running'`, only the last step
 * is `active` (everything before it is done); once `outcome === 'done'`,
 * every step is `complete`; for `'stopped'`/`'error'` the run didn't finish
 * normally, so the last step's fate is unknown — it drops `active` without
 * becoming `complete` (Codex challenge merge) and renders `pending`
 * instead, while earlier steps (which DID complete before the cutoff) stay
 * `complete`.
 */
function stepStatus(
  step: TransparencyStep,
  index: number,
  totalSteps: number,
  outcome: ChatRunStepsOutcome,
): ChainOfThoughtStepStatus {
  if (isErroredStep(step)) return 'error';
  const isLast = index === totalSteps - 1;
  if (outcome === 'running') return isLast ? 'active' : 'complete';
  if (outcome === 'done') return 'complete';
  return isLast ? 'pending' : 'complete';
}

function headerLabel(outcome: ChatRunStepsOutcome, latestStatus: string | null, stepCount: number): string {
  const plural = `${stepCount} step${stepCount === 1 ? '' : 's'}`;
  switch (outcome) {
    case 'running':
      return latestStatus || 'Working…';
    case 'stopped':
      return `Stopped · ${plural}`;
    case 'error':
      return `Failed · ${plural}`;
    case 'done':
      return plural;
  }
}

function GenerationStepBody({ step }: { step: GenerationStep }) {
  return (
    <div className="flex flex-col gap-1">
      <Text size="xs" variant="tertiary">
        {step.model} · {step.purpose}
      </Text>
      {/* A step's reasoning is already fully generated by the time it
          appears in a steps_update snapshot — never mid-character-stream
          the way the assistant's own reply text can be. */}
      {step.reasoning && (
        <ChatMarkdown variant="reasoning" streaming={false} className="wrap-break-word">
          {step.reasoning}
        </ChatMarkdown>
      )}
    </div>
  );
}

function RetrievalStepBody({ step }: { step: RetrievalStep }) {
  return (
    <div className="flex flex-col gap-1">
      <Text size="xs" variant="tertiary" className="flex flex-wrap items-center gap-x-1">
        {step.source.url ? (
          <TextLink href={step.source.url} target="_blank" rel="noreferrer" size="xs">
            {step.source.kind}
          </TextLink>
        ) : (
          <span>{step.source.kind}</span>
        )}
        {step.resultCount !== undefined && (
          <span>
            · {step.resultCount} result{step.resultCount === 1 ? '' : 's'}
          </span>
        )}
      </Text>
      {/* Literal text, never markdown — a resultPreview can be arbitrary
          source data (e.g. an error message), not trusted markdown. */}
      {step.resultPreview && (
        <Text size="xs" variant="secondary" className="wrap-break-word">
          {step.resultPreview}
        </Text>
      )}
      {step.resultItems?.map((item) =>
        item.url ? (
          <TextLink key={item.url} href={item.url} target="_blank" rel="noreferrer" size="xs">
            {item.label}
          </TextLink>
        ) : (
          <Text key={item.label} size="xs">
            {item.label}
          </Text>
        ),
      )}
    </div>
  );
}

export function ChatRunSteps({ turnId, outcome }: ChatRunStepsProps) {
  const turn = useTransparencyTurn(turnId);
  const running = outcome === 'running';

  // Open while running; historical/already-finished turns (replay) mount
  // collapsed. The effect fires ONLY on the running -> false transition
  // (dependency array is `[running]`), so a rep who reopened it mid-run is
  // never fought by a later render.
  const [open, setOpen] = useState(running);
  useEffect(() => {
    if (!running) setOpen(false);
  }, [running]);

  if (!turn || turn.steps.length === 0) return null;

  return (
    <ChainOfThought open={open} onOpenChange={setOpen} data-testid="chat-run-steps">
      <ChainOfThoughtHeader>{headerLabel(outcome, turn.latestStatus, turn.steps.length)}</ChainOfThoughtHeader>
      <ChainOfThoughtContent>
        {turn.steps.map((step, i) => (
          <ChainOfThoughtStep
            key={`${step.type}-${step.name}-${step.startMs}`}
            label={step.name}
            description={formatDuration(step.durationMs)}
            status={stepStatus(step, i, turn.steps.length, outcome)}
            icon={FLOW_ICONS.steps}
          >
            {step.type === 'generation' && <GenerationStepBody step={step} />}
            {step.type === 'retrieval' && <RetrievalStepBody step={step} />}
          </ChainOfThoughtStep>
        ))}
      </ChainOfThoughtContent>
    </ChainOfThought>
  );
}
